import { useAsyncData, useRuntimeConfig } from '#imports'
import { computed, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { TRADEIN_ERRORS } from '~/scopes/buyback/constants'

/**
 * Composable to fetch the total count of sourcing opportunities
 * Uses useAsyncData for SSR compatibility
 *
 * @returns Object containing:
 * - totalOpportunities: Ref<number> - The total count of opportunities
 * - isLoading: ComputedRef<boolean> - Loading state
 * - error: Ref<Error | null> - Error state
 */
export function useSourcingOpportunitiesTotal() {
  const httpFetch = useHttpFetch()
  const logger = useLogger()
  const { FF_BO_SELLER_TRADE_IN_OPPORTUNITIES } = useRuntimeConfig().public

  const isOpportunitiesEnabled = FF_BO_SELLER_TRADE_IN_OPPORTUNITIES === true

  const {
    data: totalOpportunities,
    status,
    error,
  } = useAsyncData(
    'sourcing-opportunities-total',
    () =>
      isOpportunitiesEnabled
        ? httpFetch('/buyback-quotes/api/v1/sourcing-opportunities')
        : Promise.resolve(null),
    {
      default: () => 0,
      transform: (data) => data?.totalCount ?? 0,
    },
  )

  watch(error, () => {
    if (!error.value) return

    logger.error(TRADEIN_ERRORS.FETCH_OPPORTUNITIES, {
      error: error.value,
      owners: ['bot-squad-circularity-offer-front'],
    })
  })

  const isLoading = computed(() => status.value === 'pending')

  return {
    totalOpportunities,
    isLoading,
    error,
    isOpportunitiesEnabled,
  }
}
